import { Stack, Typography, Button, useTheme, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { Spacing } from "@app/components/spacing";
import { Strings } from "@app/constants";
import { PATH_DASHBOARD } from "@app/routes/paths";

import { ProductInfoProps } from "../types";

const ProductInfo = ({ productData }: ProductInfoProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const InfoRow = ({ label, value }: { label: string; value: string }) => {
    const theme = useTheme();
    return (
      <Stack
        direction="row"
        gap={4}
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{
          width: { xs: "100%", md: "48%" },
        }}
      >
        <Typography sx={{ color: theme.palette.primary.main }}>
          {label}
        </Typography>
        {value ? (
          <Typography sx={{ color: theme.palette.grey[600], fontWeight: 600 }}>
            {value}
          </Typography>
        ) : (
          <Skeleton variant="text" width={50} />
        )}
      </Stack>
    );
  };

  return (
    <Stack width={{ xs: "100%", md: "50%" }} spacing={2}>
      <Typography fontWeight={600} fontSize={24}>
        {productData?.brand?.name && productData?.model?.name ? (
          `${productData?.brand?.name || ""} ${
            productData?.model?.name || ""
          }`.trim()
        ) : (
          <Skeleton variant="rounded" width={150} />
        )}
      </Typography>
      {productData?.description ? (
        <Typography fontSize={14} sx={{ color: theme.palette.grey[600] }}>
          {productData?.description}
        </Typography>
      ) : (
        <Skeleton variant="rounded" height={200} />
      )}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <Stack
          sx={{
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1, md: 4 },
          }}
        >
          <InfoRow
            label={Strings.productDetail.brand}
            value={productData?.brand?.name}
          />
          <InfoRow
            label={Strings.productDetail.model}
            value={productData?.model?.name}
          />
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1, md: 4 },
          }}
        >
          <InfoRow
            label={Strings.productDetail.year}
            value={productData?.manufacturingYear}
          />
          <InfoRow
            label={Strings.productDetail.fuelType}
            value={productData?.model?.fuelType}
          />
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1, md: 4 },
          }}
        >
          <InfoRow
            label={Strings.productDetail.variant}
            value={productData?.variant}
          />
          <InfoRow
            label={Strings.productDetail.transmission}
            value={productData?.transmission}
          />
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Stack
            direction="row"
            gap={{ xs: 1, md: 4 }}
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{
              width: { xs: "100%", md: "80%" },
            }}
          >
            <Typography sx={{ color: theme.palette.primary.main }}>
              {Strings.productDetail.distanceTravelled}
            </Typography>
            <Typography
              sx={{ color: theme.palette.grey[600], fontWeight: 600 }}
            >
              {productData?.driven}Km
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Spacing top={2} />
      <Stack direction="row" gap={2} alignItems="center">
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            fontSize: 32,
          }}
        >
          {productData?.price?.toLocaleString("en-IN", {
            currency: "INR",
            style: "currency",
            maximumFractionDigits: 0,
          })}
        </Typography>
        {productData?.originalPrice && (
          <Typography
            sx={{
              textDecoration: "line-through",
              color: theme.palette.grey[500],
              fontSize: 32,
            }}
          >
            {productData?.originalPrice?.toLocaleString("en-IN", {
              currency: "INR",
              style: "currency",
              maximumFractionDigits: 0,
            })}
          </Typography>
        )}
      </Stack>

      <Spacing top={3} />
      {productData.isSold ? (
        <Button disabled variant="soft">SOLD OUT</Button>
      ) : (
        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() =>
              navigate(PATH_DASHBOARD.testDriveProcess, {
                state: { carDetails: productData },
              })
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              width: { xs: "auto", sm: "60%", md: "100%" },
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            {Strings.button.bookTestDrive}
          </Button>

          <Button
            variant="contained"
            onClick={() =>
              navigate(`${PATH_DASHBOARD.bookNow}/${productData.id}`)
            }
            sx={{
              backgroundColor: theme.palette.primary.lighter,
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.lighter,
              width: { xs: "auto", sm: "60%", md: "100%" },
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            {Strings.button.bookNow}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export { ProductInfo };
